import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { isUniqueViolation } from '@/lib/error-handling';
import { getInvalidationKeys } from '@/lib/query-keys';
import { validateGroupName } from '@/lib/groups/validate-group-name';

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a group is created so the caller can refetch its list. */
  onSuccess?: () => void;
}

export function CreateGroupDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateGroupDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaces();
  const queryClient = useQueryClient();

  // Reset transient state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setName('');
      setError(null);
      setIsLoading(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !currentWorkspace) return;

    const validationError = validateGroupName(name);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setIsLoading(true);

    try {
      const { error: insertError } = await supabase.from('groups').insert({
        group_name: name.trim().toLowerCase(),
        workspace_id: currentWorkspace.id,
        created_by: user.id,
      });

      if (insertError) throw insertError;

      toast({ description: `Created group "${name.trim()}"` });

      // Refresh the sidebar/nav React-Query caches (the Groups page itself
      // refetches via onSuccess below — cache invalidation alone would not
      // update the page's local-state list).
      const invalidationKeys = getInvalidationKeys.afterGroupMutation(currentWorkspace.id);
      await Promise.all(
        invalidationKeys.map(key => queryClient.invalidateQueries({ queryKey: key }))
      );

      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      if (isUniqueViolation(err)) {
        // Match the wording used by AddToGroupDialog's create path.
        setError(`A group named "${name.trim()}" already exists in this workspace.`);
        return;
      }
      console.error('Error creating group:', err);
      captureError(err as Error, { feature: 'create-group' });
      toast({
        title: 'Error',
        description: 'Failed to create group. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Group</DialogTitle>
          <DialogDescription>
            Create an empty group, then open it to add contacts. The name is
            stored in lowercase to match your existing groups.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="create-group-name">Group Name</Label>
            <Input
              id="create-group-name"
              placeholder="Enter group name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              autoFocus
            />
            {error && (
              <p className="text-sm font-medium text-destructive">{error}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
